import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  LayoutGrid,
  X,
  Plus,
  Star,
} from 'lucide-react';
import {
  ATIVIDADES_WIDGET_IDS,
  GRID_COLS,
  GRID_ROWS,
  validateNoOverlap,
  type WidgetLayout,
} from '@proadv/types';

import {
  apiClient,
  type ListTasksParams,
  type TaskListItem,
  type TaskPriority,
  type TaskStatus,
} from '@/lib/api-client';
import { useSession } from '@/lib/use-session';
import { cn } from '@/lib/utils';
import { stripHtmlToText } from '@/lib/html-content';
import { TaskFormModal } from '@/components/tasks/task-form-modal';
import { TaskDetailModal } from '@/components/tasks/task-detail-modal';
import { AddWidgetMenu } from '@/components/dashboard/add-widget-menu';
import { DashboardGrid } from '@/components/dashboard/dashboard-grid';
import { ATIVIDADES_DEFAULT_LAYOUT } from '@/components/dashboard/atividades-widgets/default-layout';
import { GlassSelect } from '@/components/ui/glass-select';

const KNOWN = new Set<string>(ATIVIDADES_WIDGET_IDS);

const STATUS_LABEL: Record<TaskStatus, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  canceled: 'Cancelada',
};
const PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};
const PRIORITY_COLOR: Record<TaskPriority, string> = {
  low: 'text-[var(--fg-muted)]',
  normal: 'text-[var(--fg)]',
  high: 'text-amber-500',
  urgent: 'text-red-500',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function urgencyClass(fatalIso: string | null): string {
  if (!fatalIso) return 'text-[var(--fg-muted)]';
  const d = new Date(fatalIso).getTime();
  const now = Date.now();
  const days = (d - now) / (1000 * 60 * 60 * 24);
  if (days < 0) return 'text-red-600 font-semibold';
  if (days <= 3) return 'text-red-500 font-medium';
  if (days <= 7) return 'text-amber-500';
  return 'text-[var(--fg)]';
}

function sanitizeLayout(input: WidgetLayout[] | null | undefined): WidgetLayout[] {
  if (!input || input.length === 0) return ATIVIDADES_DEFAULT_LAYOUT;
  const out = input
    .filter((w) => KNOWN.has(w.widgetId))
    .map((w) => ({
      widgetId: w.widgetId,
      x: Math.max(0, Math.min(GRID_COLS - 1, Math.floor(w.x))),
      y: Math.max(0, Math.min(GRID_ROWS - 1, Math.floor(w.y))),
      w: Math.max(1, Math.min(4, Math.floor(w.w))),
      h: Math.max(1, Math.min(3, Math.floor(w.h))),
    }));
  return out.length > 0 ? out : ATIVIDADES_DEFAULT_LAYOUT;
}

function findFreeSpot(layout: WidgetLayout[]): { x: number; y: number } {
  for (let y = 0; y < GRID_ROWS; y++) {
    for (let x = 0; x < GRID_COLS; x++) {
      const tentative: WidgetLayout = { widgetId: '__probe__', x, y, w: 1, h: 1 };
      const conflict = validateNoOverlap([...layout, tentative]);
      if (!conflict) return { x, y };
    }
  }
  return { x: 0, y: GRID_ROWS - 1 };
}

export default function AtividadesPage() {
  const queryClient = useQueryClient();
  const session = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskIdFromQs = searchParams.get('taskId');
  const newTaskFromQs = searchParams.get('newTask') === '1';
  const prefillCaseId = searchParams.get('caseId');
  const prefillClientId = searchParams.get('clientId');
  const prefillIntimacaoId = searchParams.get('intimacaoId');

  // Grid state
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draftLayout, setDraftLayout] = useState<WidgetLayout[] | null>(null);

  // Lista state
  const [filters, setFilters] = useState<ListTasksParams>({
    page: 1,
    pageSize: 50,
    orderBy: 'fatalDate',
    orderDir: 'asc',
  });
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(newTaskFromQs);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(taskIdFromQs);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (taskIdFromQs && taskIdFromQs !== detailTaskId) {
      setDetailTaskId(taskIdFromQs);
    }
  }, [taskIdFromQs, detailTaskId]);

  useEffect(() => {
    if (newTaskFromQs) setShowCreate(true);
  }, [newTaskFromQs]);

  // Layout queries
  const layoutQuery = useQuery({
    queryKey: ['me', 'dashboard-layout', 'atividades'],
    queryFn: () => apiClient.meGetDashboardLayout('atividades'),
    staleTime: 1000 * 60,
  });

  const savedLayout = useMemo(
    () => sanitizeLayout(layoutQuery.data?.layout),
    [layoutQuery.data],
  );

  const activeLayout = mode === 'edit' && draftLayout ? draftLayout : savedLayout;

  const saveMutation = useMutation({
    mutationFn: (layout: WidgetLayout[]) =>
      apiClient.meUpdateDashboardLayout('atividades', layout),
    onSuccess: (res) => {
      queryClient.setQueryData(['me', 'dashboard-layout', 'atividades'], {
        layout: res.layout,
      });
      setMode('view');
      setDraftLayout(null);
    },
  });

  function enterEditMode() {
    setDraftLayout(savedLayout.map((w) => ({ ...w })));
    setMode('edit');
  }

  function cancelEdit() {
    setMode('view');
    setDraftLayout(null);
    saveMutation.reset();
  }

  function saveEdit() {
    if (!draftLayout) return;
    const overlap = validateNoOverlap(draftLayout);
    if (overlap) return;
    saveMutation.mutate(draftLayout);
  }

  function handleLayoutChange(next: WidgetLayout[]) {
    if (mode !== 'edit') return;
    setDraftLayout(next);
  }

  function handleRemove(widgetId: string) {
    if (mode !== 'edit') return;
    setDraftLayout((prev) =>
      (prev ?? savedLayout).filter((w) => w.widgetId !== widgetId),
    );
  }

  function handleAdd(widgetId: string) {
    setDraftLayout((prev) => {
      const base = prev ?? savedLayout;
      if (base.some((w) => w.widgetId === widgetId)) return base;
      const spot = findFreeSpot(base);
      return [...base, { widgetId, x: spot.x, y: spot.y, w: 1, h: 1 }];
    });
  }

  useEffect(() => {
    if (mode !== 'edit') return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      } else if ((e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        saveEdit();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, draftLayout]);

  // Lista queries
  const listQ = useQuery({
    queryKey: ['tasks', 'list', filters, search],
    queryFn: () =>
      apiClient.tasksList({ ...filters, search: search || undefined }),
  });

  const categoriesQ = useQuery({
    queryKey: ['task-categories'],
    queryFn: () => apiClient.taskCategoriesList({ active: 'true' }),
  });

  const completeMut = useMutation({
    mutationFn: (id: string) => apiClient.tasksComplete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
  const uncompleteMut = useMutation({
    mutationFn: (id: string) => apiClient.tasksUncomplete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
  const togglePinMut = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      apiClient.tasksUpdate(id, { pinned }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const items = listQ.data?.items ?? [];
  const presentIds = activeLayout.map((w) => w.widgetId);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-tour="atividades-nova-tarefa"
            onClick={() => setShowCreate(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Nova tarefa
          </button>
          <button
            type="button"
            onClick={() => (mode === 'edit' ? cancelEdit() : enterEditMode())}
            aria-label={mode === 'edit' ? 'Sair do modo edição' : 'Editar dashboard'}
            title={mode === 'edit' ? 'Sair do modo edição' : 'Editar dashboard'}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md border transition',
              'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)] hover:text-[var(--fg)]',
              mode === 'edit' && 'text-[var(--accent)]',
            )}
          >
            {mode === 'edit' ? (
              <X className="h-4 w-4" />
            ) : (
              <LayoutGrid className="h-4 w-4" />
            )}
          </button>
        </div>
      </header>

      {/* Barra de edição */}
      {mode === 'edit' && (
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2',
            'border-[var(--accent)]/40 bg-[var(--accent)]/5',
          )}
        >
          <p className="text-xs text-[var(--fg-muted)]">
            Modo edição: arraste para mover, alças para redimensionar.
            <span className="ml-2 opacity-60">Esc cancela · Ctrl+S salva</span>
          </p>
          <div className="flex items-center gap-2">
            <AddWidgetMenu pageId="atividades" presentIds={presentIds} onAdd={handleAdd} />
            <button
              type="button"
              onClick={cancelEdit}
              className={cn(
                'rounded-md border px-3 py-1.5 text-xs font-medium transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)] hover:text-[var(--fg)]',
              )}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={saveMutation.isPending}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition',
                'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
                saveMutation.isPending && 'opacity-60',
              )}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      )}

      {saveMutation.isError && (
        <p className="text-xs text-red-500">Falha ao salvar o layout. Tente novamente.</p>
      )}

      {/* Grid de widgets */}
      <DashboardGrid
        pageId="atividades"
        layout={activeLayout}
        mode={mode}
        onLayoutChange={handleLayoutChange}
        onRemove={handleRemove}
      />

      <div className="border-t border-[var(--border)]" />

      {/* Lista de tarefas (fixa, fora do grid) */}
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-[var(--fg)]">Tarefas</h2>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="text"
            data-tour="atividades-busca"
            placeholder="Buscar por título ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] flex-1 min-w-[220px]"
          />
          <div data-tour="atividades-filtro-status">
            <GlassSelect
              aria-label="Filtrar por status"
              value={filters.status ?? ''}
              onChange={(val) =>
                setFilters((f) => ({
                  ...f,
                  status: (val || undefined) as TaskStatus | undefined,
                }))
              }
              options={[
                { value: '', label: 'Todos status' },
                ...Object.entries(STATUS_LABEL).map(([k, v]) => ({ value: k, label: v })),
              ]}
              className="min-w-[140px]"
            />
          </div>
          <div data-tour="atividades-filtro-prioridade">
            <GlassSelect
              aria-label="Filtrar por prioridade"
              value={filters.priority ?? ''}
              onChange={(val) =>
                setFilters((f) => ({
                  ...f,
                  priority: (val || undefined) as TaskPriority | undefined,
                }))
              }
              options={[
                { value: '', label: 'Todas prioridades' },
                ...Object.entries(PRIORITY_LABEL).map(([k, v]) => ({ value: k, label: v })),
              ]}
              className="min-w-[160px]"
            />
          </div>
          <div data-tour="atividades-filtro-responsavel">
            <GlassSelect
              aria-label="Filtrar por responsável"
              value={filters.assigneeUserId ?? ''}
              onChange={(val) =>
                setFilters((f) => ({
                  ...f,
                  assigneeUserId: (val || undefined) as 'me' | string | undefined,
                }))
              }
              options={[
                { value: '', label: 'Todos responsáveis' },
                { value: 'me', label: 'Atribuídas a mim' },
              ]}
              className="min-w-[170px]"
            />
          </div>
        </div>

        {/* Tabela */}
        <div data-tour="atividades-tabela" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg overflow-hidden shadow-[var(--shadow-card)]">
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-[var(--fg-muted)] text-xs uppercase">
              <tr>
                <th className="px-3 py-2 w-8" />
                <th className="px-3 py-2 w-8" />
                <th className="px-3 py-2 text-left">Tarefa</th>
                <th className="px-3 py-2 text-left">Partes</th>
                <th className="px-3 py-2 text-left w-32">Compromisso</th>
                <th className="px-3 py-2 text-left w-32">Prazo fatal</th>
                <th className="px-3 py-2 text-left w-40">Responsáveis</th>
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-[var(--fg-muted)]">
                    Carregando...
                  </td>
                </tr>
              )}
              {!listQ.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-[var(--fg-muted)]">
                    Nenhuma tarefa encontrada.
                  </td>
                </tr>
              )}
              {items.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  onTogglePin={(id, pinned) => togglePinMut.mutate({ id, pinned: !pinned })}
                  onComplete={(id) => completeMut.mutate(id)}
                  onUncomplete={(id) => uncompleteMut.mutate(id)}
                  onOpen={(id) => {
                    setDetailTaskId(id);
                    setSearchParams({ taskId: id });
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {showCreate && (
        <TaskFormModal
          categories={categoriesQ.data ?? []}
          onClose={() => {
            setShowCreate(false);
            if (
              searchParams.has('newTask') ||
              searchParams.has('caseId') ||
              searchParams.has('clientId') ||
              searchParams.has('intimacaoId')
            ) {
              searchParams.delete('newTask');
              searchParams.delete('caseId');
              searchParams.delete('clientId');
              searchParams.delete('intimacaoId');
              setSearchParams(searchParams);
            }
          }}
          onCreated={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
          currentUserId={session.user?.id ?? ''}
          tenantId={session.tenantId ?? ''}
          caseId={prefillCaseId}
          clientId={prefillClientId}
          intimacaoId={prefillIntimacaoId}
        />
      )}

      {editingTaskId && (
        <TaskFormModal
          categories={categoriesQ.data ?? []}
          editingTaskId={editingTaskId}
          onClose={() => setEditingTaskId(null)}
          onCreated={() => {
            setEditingTaskId(null);
            void queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
          currentUserId={session.user?.id ?? ''}
          tenantId={session.tenantId ?? ''}
        />
      )}

      <TaskDetailModal
        taskId={detailTaskId}
        open={!!detailTaskId}
        onClose={() => {
          setDetailTaskId(null);
          searchParams.delete('taskId');
          setSearchParams(searchParams);
        }}
        onEdit={(id) => {
          setDetailTaskId(null);
          searchParams.delete('taskId');
          setSearchParams(searchParams);
          setEditingTaskId(id);
        }}
      />
    </div>
  );
}

function TaskRow({
  task,
  onTogglePin,
  onComplete,
  onUncomplete,
  onOpen,
}: {
  task: TaskListItem;
  onTogglePin: (id: string, pinned: boolean) => void;
  onComplete: (id: string) => void;
  onUncomplete: (id: string) => void;
  onOpen: (id: string) => void;
}) {
  const isDone = task.status === 'completed';
  return (
    <tr
      className={cn(
        'border-t border-[var(--border)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] cursor-pointer',
        isDone && 'opacity-60',
      )}
      onClick={() => onOpen(task.id)}
    >
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onTogglePin(task.id, task.pinned)}
          className="text-[var(--fg-muted)] hover:text-amber-500"
          aria-label={task.pinned ? 'Desafixar' : 'Fixar'}
        >
          {task.pinned ? (
            <Star className="h-4 w-4 fill-amber-400 text-amber-500" />
          ) : (
            <Star className="h-4 w-4" />
          )}
        </button>
      </td>
      <td className="px-3 py-2">
        <span
          className={cn(
            'inline-block h-2 w-2 rounded-full',
            isDone
              ? 'bg-emerald-500'
              : task.status === 'in_progress'
                ? 'bg-blue-500'
                : 'bg-slate-300',
          )}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          {task.category && (
            <span
              className="inline-block rounded-full px-2 py-0.5 text-xs font-medium text-white"
              style={{ background: task.category.color }}
            >
              {task.category.name}
            </span>
          )}
          <span
            className={cn(
              'font-medium',
              PRIORITY_COLOR[task.priority],
              isDone && 'line-through',
            )}
          >
            {task.title}
          </span>
        </div>
        {task.description && (
          <p className="text-xs text-[var(--fg-muted)] mt-0.5 line-clamp-1">
            {stripHtmlToText(task.description)}
          </p>
        )}
      </td>
      <td className="px-3 py-2 text-[var(--fg)]">{task.client?.name ?? '—'}</td>
      <td className="px-3 py-2 text-[var(--fg)]">{formatDate(task.dueDate)}</td>
      <td className={cn('px-3 py-2', urgencyClass(task.fatalDate))}>
        {formatDate(task.fatalDate)}
      </td>
      <td className="px-3 py-2">
        <div className="flex -space-x-1">
          {task.assignees.slice(0, 3).map((a) => (
            <span
              key={a.id}
              title={a.name}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[var(--surface)] text-xs font-medium text-[var(--fg)] ring-2 ring-[var(--card-bg)]"
            >
              {a.name.slice(0, 1).toUpperCase()}
            </span>
          ))}
          {task.assignees.length > 3 && (
            <span className="text-xs text-[var(--fg-muted)] ml-2">
              +{task.assignees.length - 3}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        {isDone ? (
          <button
            type="button"
            onClick={() => onUncomplete(task.id)}
            className="text-xs text-[var(--fg-muted)] hover:text-[var(--fg)]"
          >
            Reabrir
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onComplete(task.id)}
            className="text-xs text-emerald-600 hover:text-emerald-800"
          >
            Concluir
          </button>
        )}
      </td>
    </tr>
  );
}


